/**
 * NotificationBell — Real, polled implementation.
 *
 *  - Fetches /api/notifications/unread-count every 30s for the badge
 *  - Click → /api/notifications (with status filter tabs: All / Unread / Mentions / Tickets)
 *  - Click row → mark read + navigate to the source_link
 *  - "Mark all as read" button
 *  - Replaces NotificationBellPrototype.js
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bell, AtSign, Ticket as TicketIcon, MessageCircle, Inbox, CheckCheck, ExternalLink, X, Presentation, IndianRupee, Ticket as VoucherIcon, PhoneCall } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const POLL_INTERVAL_MS = 30000;

const TYPE_META = {
  mention:         { Icon: AtSign,        color: 'text-violet-600',  bg: 'bg-violet-50',  border: 'border-violet-200',  label: 'Mention' },
  ticket_assigned: { Icon: TicketIcon,    color: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-200',    label: 'Ticket' },
  ticket_updated:  { Icon: TicketIcon,    color: 'text-rose-500',    bg: 'bg-rose-50',    border: 'border-rose-100',    label: 'Ticket' },
  system:          { Icon: MessageCircle, color: 'text-slate-600',   bg: 'bg-slate-50',   border: 'border-slate-200',   label: 'System' },
  demo_invoice_review: { Icon: IndianRupee, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Review' },
  voucher_low_stock: { Icon: VoucherIcon, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Vouchers' },
  approval_pending: { Icon: Presentation, color: 'text-violet-700',  bg: 'bg-violet-50',  border: 'border-violet-200',  label: 'Approval' },
  callback_cold: { Icon: PhoneCall, color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200', label: 'Call Back' },
  callback_summary: { Icon: PhoneCall, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Call Back' },
  callback_lost_early: { Icon: PhoneCall, color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Lost early' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en', { day: '2-digit', month: 'short' });
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('unread');
  const intervalRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      setUnread(r.data?.unread || 0);
    } catch {
      /* ignore — auth issues, etc */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications', { params: { status: tab === 'unread' ? 'unread' : 'all', limit: 50 } });
      setRows(r.data?.rows || []);
      setUnread(r.data?.unread_count ?? unread);
    } catch (err) {
      // 401/403 — bail silently
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    refreshCount();
    intervalRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [refreshCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const filtered = useMemo(() => {
    if (tab === 'all' || tab === 'unread') return rows;
    if (tab === 'mentions') return rows.filter(r => r.type === 'mention');
    if (tab === 'tickets') return rows.filter(r => r.type?.startsWith('ticket'));
    return rows;
  }, [rows, tab]);

  const onClickRow = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setRows(rs => rs.map(r => r.id === n.id ? { ...r, read: true } : r));
        setUnread(u => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
    if (n.source_link) navigate(n.source_link);
    setOpen(false);
  };

  const markAllRead = async () => {
    try {
      const r = await api.post('/notifications/mark-all-read');
      setRows(rs => rs.map(x => ({ ...x, read: true })));
      setUnread(0);
      const n = r.data?.updated || 0;
      // soft toast via title attr only, no need for sonner here
      if (n === 0) return;
    } catch { /* ignore */ }
  };

  const dismiss = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setRows(rs => rs.filter(r => r.id !== id));
      refreshCount();
    } catch { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-slate-600" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center"
              data-testid="notification-bell-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[400px] p-0" data-testid="notification-bell-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Inbox className="w-4 h-4" /> Notifications
            {unread > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold">{unread} new</span>}
          </p>
          <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={markAllRead} disabled={unread === 0} data-testid="notification-mark-all">
            <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {[
            { id: 'unread', label: 'Unread' },
            { id: 'all', label: 'All' },
            { id: 'mentions', label: 'Mentions' },
            { id: 'tickets', label: 'Tickets' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 text-[11px] py-2 font-medium transition-colors ${tab === t.id ? 'border-b-2 border-violet-600 text-violet-700' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`notification-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {loading && <p className="text-center text-xs text-slate-400 py-8">Loading…</p>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-10 px-4">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-400">{tab === 'unread' ? "You're all caught up!" : 'No notifications yet'}</p>
            </div>
          )}
          {!loading && filtered.map(n => {
            const meta = TYPE_META[n.type] || TYPE_META.system;
            const Icon = meta.Icon;
            return (
              <div key={n.id} onClick={() => onClickRow(n)}
                className={`group px-3 py-2 border-b cursor-pointer hover:bg-violet-50/40 transition-colors ${n.read ? 'opacity-70' : ''}`}
                data-testid={`notification-row-${n.type}`}>
                <div className="flex items-start gap-2">
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.bg} ${meta.color} border ${meta.border}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800 truncate">{n.title}</p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" title="Unread" />}
                        <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                        <button onClick={(e) => dismiss(e, n.id)} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100" title="Dismiss">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {n.body && <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2 leading-relaxed">{n.body}</p>}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-slate-500">
                        {n.actor_name && <>by <span className="font-medium">{n.actor_name}</span></>}
                      </span>
                      {n.source_link && <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-violet-600" />}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
